import { useLoaderData, useLocation } from 'react-router';
import { AddOnVisibilityOperationType } from '../../../../../common/enums.js';
import { useAddOnVisibility } from '../../../../../common/hooks/useAddOnVisibility.js';
import { useSearchParams } from '../../../../../common/hooks/useSearchParams';
import { ensureNonNullable } from '../../../../../common/utils/validationUtils.js';
import { SubscriptionChangeConfirmation } from '../../../../../components/OrderSubscriptionSelection/SubscriptionChangeConfirmation.js';
import { Spinner } from '../../../siteUtils.js';
import type { MobileSubUpdateConfirmationLoaderData } from '../../../../../common/loaders';
import type { AddedAddon } from '../../../../../common/types/addon.js';
import type { CampaignAssociation } from '../../../../../generated/api/campaignAssociation.js';
import type { CommercialProduct } from '../../../../../generated/api/commercialProduct.js';
import type { Offer } from '../../../../../generated/api/offer.js';
import type { Subscription } from '../../../../../generated/api/subscription.js';

export interface SubscriptionUpdateNavigationState {
  selectedCommercialProduct: CommercialProduct;
  selectedServiceFee?: { price: number; productId: string };
  subscription: Subscription;
  selectedAddOns?: AddedAddon[];
  campaignAssociation?: CampaignAssociation;
  campaignDescription?: string;
  onlineModelName: string;
  offer: Offer;
  previousOffer?: Offer;
  voucher?: string;
}

const validateMandatoryParameters = (
  subscription: Subscription,
  selectedCommercialProduct: CommercialProduct
): void => {
  ensureNonNullable(subscription, 'subscription');
  ensureNonNullable(selectedCommercialProduct, 'selectedCommercialProduct');
};

export const MobileSubUpdateConfirmationPath = () => {
  const location = useLocation();
  const {
    selectedCommercialProduct,
    subscription,
    selectedAddOns,
    campaignAssociation,
    campaignDescription,
    selectedServiceFee,
    onlineModelName,
    offer,
    previousOffer,
    voucher,
  } = (location.state as SubscriptionUpdateNavigationState) || {};
  const { addOnRules, addOnRulesMobilePbx, pbxSolutions } = useLoaderData() as MobileSubUpdateConfirmationLoaderData;
  const { mdmId } = useSearchParams<{ mdmId?: string }>();

  validateMandatoryParameters(subscription, selectedCommercialProduct);

  const { addOnVisibilities, addOnVisibilitiesError } = useAddOnVisibility(
    selectedCommercialProduct?.commercialProductCode,
    AddOnVisibilityOperationType.CHANGE,
    undefined,
    mdmId
  );

  if (addOnVisibilitiesError) {
    throw new Error(
      `Cannot fetch addon visibilities for commercial product ${selectedCommercialProduct?.commercialProductCode}}`
    );
  }

  if (!addOnVisibilities) {
    return <Spinner />;
  }

  return (
    <SubscriptionChangeConfirmation
      selectedCommercialProduct={selectedCommercialProduct}
      subscription={subscription}
      addOnRules={addOnRules}
      addOnRulesMobilePbx={addOnRulesMobilePbx}
      selectedAddOns={selectedAddOns}
      campaignAssociation={campaignAssociation}
      addOnVisibilities={addOnVisibilities}
      campaignDescription={campaignDescription}
      pbxSolutions={pbxSolutions || []}
      selectedServiceFee={selectedServiceFee}
      onlineModelName={onlineModelName}
      offer={offer}
      previousOffer={previousOffer}
      voucher={voucher}
    />
  );
};
